import { Component } from 'react';
import { store } from 'src/store.js';

// Error tracking to prevent infinite loops
const errorTracker = {
	lastError: null,
	lastErrorTime: 0,
	errorCount: 0,
	maxErrorsPerMinute: 5,
	resetTimeout: 60000, // 1 minute
	errorsByComponent: new Map(), // Track errors by component
	componentErrorThreshold: 2, // Reduced threshold to catch loops faster
	globalErrorThreshold: 10, // Max total errors before forcing a page refresh
	lastRefreshTime: 0,
	minRefreshInterval: 300000, // 5 minutes - prevent excessive refreshes
};

export default class ErrorHandler extends Component {
	static getDerivedStateFromError(error) {
		// Update state to trigger fallback UI
		return { hasError: true, error };
	}

	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
			reportDownloaded: false,
		};
	}

	componentDidCatch(error, errorInfo) {
		this.setState({ errorInfo });

		// Check if we're getting too many errors too quickly
		const now = Date.now();
		const errorString = error.toString();
		const isSameError = errorString === errorTracker.lastError?.toString();

		// Extract component name from error info
		const componentStackLines = errorInfo.componentStack.split('\n');
		const componentName =
			componentStackLines.length > 1
				? componentStackLines[1].trim().replace(/^in\s+/, '')
				: 'Unknown';

		// Reset error count if it's been more than a minute since the last error
		if (now - errorTracker.lastErrorTime > errorTracker.resetTimeout) {
			errorTracker.errorCount = 0;
			errorTracker.errorsByComponent.clear();
		}

		// Track errors by component
		const componentErrors =
			errorTracker.errorsByComponent.get(componentName) || 0;
		errorTracker.errorsByComponent.set(componentName, componentErrors + 1);

		// Increment error count
		errorTracker.errorCount++;
		errorTracker.lastError = error;
		errorTracker.lastErrorTime = now;

		// Check if we're in an infinite loop for this component
		const isComponentInfiniteLoop =
			errorTracker.errorsByComponent.get(componentName) >=
			errorTracker.componentErrorThreshold;

		// Check if we're experiencing a global error storm
		const isGlobalErrorStorm =
			errorTracker.errorCount >= errorTracker.globalErrorThreshold;

		// Handle global error storm - force page refresh if we haven't refreshed recently
		if (
			isGlobalErrorStorm &&
			now - errorTracker.lastRefreshTime > errorTracker.minRefreshInterval
		) {
			console.error(
				`Severe error storm detected! Forcing page refresh to recover.`
			);
			errorTracker.lastRefreshTime = now;

			// Set a flag to indicate we're forcing a refresh due to errors
			sessionStorage.setItem('errorHandler_forcedRefresh', 'true');
			sessionStorage.setItem('errorHandler_refreshTime', now.toString());

			// Force refresh after a short delay to allow logging
			setTimeout(() => {
				window.location.reload();
			}, 500);

			this.logErrorStorm();
			return;
		}

		// Only log and notify if we're not in an error storm
		if (errorTracker.errorCount <= errorTracker.maxErrorsPerMinute) {
			this.logError(error, errorInfo);

			// If we detect a component in an infinite loop, log a specific message
			if (isComponentInfiniteLoop) {
				console.error(
					`Possible infinite loop detected in component: ${componentName}`
				);
				console.error(
					`This may be caused by state updates during rendering or effects running repeatedly`
				);

				// Force break the loop by setting a flag in localStorage
				localStorage.setItem('errorHandler_breakLoop', componentName);

				// Log a more specific error storm for this component
				this.logComponentErrorStorm(componentName, errorString);
			}
		} else if (
			errorTracker.errorCount ===
			errorTracker.maxErrorsPerMinute + 1
		) {
			// Log one final message about the error storm
			console.error(
				`Error storm detected! Suppressing further error notifications.`
			);
			this.logErrorStorm();
		}
	}

	logErrorStorm() {
		console.error('Error storm detected: Multiple errors occurring rapidly.');
	}

	logComponentErrorStorm(componentName, errorString) {
		console.error(
			`Infinite loop detected in component "${componentName}": ${errorString}`
		);
	}

	logError(error, errorInfo) {
		console.error(error, errorInfo);
	}

	refreshApp = () => {
		window.location.reload();
	};

	generateReport = () => {
		const { error, errorInfo } = this.state;
		const now = new Date();

		// Gather Redux state (sanitize sensitive fields)
		let reduxState = {};
		try {
			const fullState = store.getState();
			const user = fullState?.user?.currentUser;
			reduxState = {
				currentUser: user
					? {
							id: user.id,
							companyId: user.companyId,
							cognitoId: user.cognitoId
								? `${user.cognitoId.slice(0, 4)}...[REDACTED]`
								: null,
							accessToken: '[REDACTED]',
							role: user.role,
							displayAs: user.displayAs,
							emailAddress: user.emailAddress,
							firstName: user.firstName,
							lastName: user.lastName,
							title: user.title,
							userGroupId: user.userGroupId,
							departmentId: user.departmentId,
							department: user.department?.name || user.department,
							subCompanyId: user.subCompanyId,
							active: user.active,
							languageCode: user.languageCode,
							currency: user.currency,
							lastLogin: user.lastLogin,
							authMethod: user.authMethod,
							employeeId: user.employeeId,
							isScopedUser: user.isScopedUser,
							incentiveEligible: user.incentiveEligible,
							inviteStatus: user.inviteStatus,
							totalReferrals: user.totalReferrals,
							points: user.points,
							featureFlags: user.featureFlags,
							enableJobNotifications: user.enableJobNotifications,
							enableRecognition: user.enableRecognition,
							createdById: user.createdById,
							contacts: user.contacts
								? `[${user.contacts.length} contacts]`
								: null,
							careerProfile: user.careerProfile ? '[PRESENT - REDACTED]' : null,
							accountClaim: user.accountClaim
								? {
										id: user.accountClaim.id,
										active: user.accountClaim.active,
										eligible: user.accountClaim.eligible,
										dateOfBirth: '[REDACTED]',
									}
								: null,
							company: user.company
								? {
										id: user.company.id,
										name: user.company.name,
										host: user.company.host,
										accountType: user.company.accountType,
										brandColor: user.company.brandColor,
										enableRecognition: user.company.enableRecognition,
										enableCareerProfile: user.company.enableCareerProfile,
										enableGeneralReferrals: user.company.enableGeneralReferrals,
										enableLead: user.company.enableLead,
										allowSelfReferrals: user.company.allowSelfReferrals,
										allowInternalMobility: user.company.allowInternalMobility,
										disableSAMLLogin: user.company.disableSAMLLogin,
										disableManagerPermissions:
											user.company.disableManagerPermissions,
										hideLoginForm: user.company.hideLoginForm,
										countryCode: user.company.countryCode,
									}
								: null,
						}
					: null,
				nullFields: user
					? {
							user: Object.keys(user).filter(
								(k) => user[k] === null || user[k] === undefined
							),
							accountClaim: user.accountClaim
								? Object.keys(user.accountClaim).filter(
										(k) =>
											user.accountClaim[k] === null ||
											user.accountClaim[k] === undefined
									)
								: 'accountClaim is null',
							company: user.company
								? Object.keys(user.company).filter(
										(k) =>
											user.company[k] === null || user.company[k] === undefined
									)
								: 'company is null',
						}
					: null,
			};
		} catch {
			reduxState = { error: 'Could not read Redux state' };
		}

		// Gather localStorage keys (not values for sensitive ones)
		const localStorageSnapshot = {};
		try {
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				const sensitiveKeys = [
					'token',
					'jwt',
					'password',
					'secret',
					'credential',
				];
				if (sensitiveKeys.some((s) => key.toLowerCase().includes(s))) {
					localStorageSnapshot[key] = '[REDACTED]';
				} else {
					const value = localStorage.getItem(key);
					localStorageSnapshot[key] =
						value?.length > 200
							? `${value.slice(0, 200)}...[truncated]`
							: value;
				}
			}
		} catch {
			localStorageSnapshot.error = 'Could not read localStorage';
		}

		// Gather router state
		let routerState = {};
		try {
			routerState = {
				pathname: window.location.pathname,
				search: window.location.search,
				hash: window.location.hash,
				referrer: document.referrer || null,
				historyLength: window.history.length,
				historyState: window.history.state,
				pendingRedirectURL: sessionStorage.getItem('pendingRedirectURL'),
				authType: localStorage.getItem('authType'),
				samlLoginInProgress: localStorage.getItem('samlLoginInProgress'),
			};
		} catch {
			routerState = { error: 'Could not read router state' };
		}

		const report = {
			timestamp: now.toISOString(),
			url: window.location.href,
			route: window.location.pathname,
			userAgent: navigator.userAgent,
			screenSize: `${window.screen.width}x${window.screen.height}`,
			viewportSize: `${window.innerWidth}x${window.innerHeight}`,
			error: {
				message: error?.message,
				name: error?.name,
				stack: error?.stack,
			},
			componentStack: errorInfo?.componentStack,
			errorTracker: {
				errorCount: errorTracker.errorCount,
				lastErrorTime: errorTracker.lastErrorTime
					? new Date(errorTracker.lastErrorTime).toISOString()
					: null,
				errorsByComponent: Object.fromEntries(errorTracker.errorsByComponent),
			},
			routerState,
			reduxState,
			localStorage: localStorageSnapshot,
			sessionStorage: {
				errorHandler_forcedRefresh: sessionStorage.getItem(
					'errorHandler_forcedRefresh'
				),
				errorHandler_refreshTime: sessionStorage.getItem(
					'errorHandler_refreshTime'
				),
			},
			performance: {
				pageLoadTime: Math.round(performance.now()),
				memory: performance.memory
					? {
							usedJSHeapSize: `${Math.round(performance.memory.usedJSHeapSize / 1024 / 1024)}MB`,
							totalJSHeapSize: `${Math.round(performance.memory.totalJSHeapSize / 1024 / 1024)}MB`,
						}
					: 'unavailable',
			},
		};

		const blob = new Blob([JSON.stringify(report, null, 2)], {
			type: 'application/json',
		});
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `error-report-${now.toISOString().replace(/[:.]/g, '-')}.json`;
		a.click();
		URL.revokeObjectURL(url);
		this.setState({ reportDownloaded: true });
	};

	resetAndStartFresh = () => {
		// Clear localStorage items that can cause redirect loops or stale state
		localStorage.removeItem('samlLoginInProgress');
		localStorage.removeItem('authType');
		localStorage.removeItem('lastLoginValue');
		localStorage.removeItem('showFirstTimePopup');
		localStorage.removeItem('mobileSource');
		localStorage.removeItem('errorHandler_breakLoop');
		sessionStorage.removeItem('errorHandler_forcedRefresh');
		sessionStorage.removeItem('errorHandler_refreshTime');

		// Clear all sessionStorage
		sessionStorage.clear();

		const navigateToLogout = () => {
			window.location.href = '/logout';
		};

		// Clear Redux persisted state (localforage / IndexedDB)
		try {
			if (window.indexedDB) {
				const request = window.indexedDB.deleteDatabase('localforage');
				request.onsuccess = navigateToLogout;
				request.onerror = navigateToLogout;
				request.onblocked = navigateToLogout;
				// Safety timeout in case none of the callbacks fire
				setTimeout(navigateToLogout, 3000);
			} else {
				navigateToLogout();
			}
		} catch {
			navigateToLogout();
		}
	};

	render() {
		// Check if we need to break an infinite loop
		const breakLoopComponent = localStorage.getItem('errorHandler_breakLoop');
		const forcedRefresh = sessionStorage.getItem('errorHandler_forcedRefresh');

		// Only render children if there's no error
		// We no longer use IgnoreErrors to determine whether to render children
		// This ensures all errors trigger the fallback UI and break potential infinite loops
		if (!this.state.hasError) {
			return <>{this.props.children}</>;
		}

		// Clear the break loop flag when rendering the error UI
		if (breakLoopComponent) {
			localStorage.removeItem('errorHandler_breakLoop');
		}

		// Clear the forced refresh flag when rendering the error UI
		if (forcedRefresh) {
			sessionStorage.removeItem('errorHandler_forcedRefresh');
		}

		// Render fallback UI
		return (
			<div className="error-wrapper">
				<div className="error-container text-center">
					<h1>Woops! Something Went Wrong.</h1>
					{breakLoopComponent && (
						<div style={{ color: 'red', margin: '10px 0' }}>
							<p>
								An infinite loop was detected in component:{' '}
								<strong>{breakLoopComponent}</strong>
							</p>
							<p>
								This may be caused by state updates during rendering or effects
								running repeatedly.
							</p>
						</div>
					)}
					{forcedRefresh && (
						<div style={{ color: 'orange', margin: '10px 0' }}>
							<p>
								The application was automatically refreshed due to multiple
								errors.
							</p>
							<p>If this happens repeatedly, please contact support.</p>
						</div>
					)}
					<div style={{ maxWidth: 480, margin: '0 auto', textAlign: 'left' }}>
						{/* Step 1 */}
						<div
							style={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: 12,
								marginTop: 24,
							}}
						>
							<span
								style={{
									flexShrink: 0,
									width: 28,
									height: 28,
									borderRadius: '50%',
									background: '#e2e8f0',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: 14,
									fontWeight: 700,
									color: '#475569',
								}}
							>
								1
							</span>
							<div>
								<p
									style={{
										margin: '4px 0 6px',
										fontSize: '0.95rem',
										fontWeight: 600,
										color: '#334155',
									}}
								>
									Try refreshing the page
								</p>
								<button
									type="button"
									onClick={this.refreshApp}
									style={{
										padding: '8px 20px',
										fontSize: '0.85rem',
										fontWeight: 600,
										color: '#475569',
										background: '#f1f5f9',
										border: 'none',
										borderRadius: 8,
										cursor: 'pointer',
									}}
								>
									Refresh Page
								</button>
							</div>
						</div>

						{/* Step 2 */}
						<div
							style={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: 12,
								marginTop: 20,
							}}
						>
							<span
								style={{
									flexShrink: 0,
									width: 28,
									height: 28,
									borderRadius: '50%',
									background: '#e2e8f0',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: 14,
									fontWeight: 700,
									color: '#475569',
								}}
							>
								2
							</span>
							<div>
								<p
									style={{
										margin: '4px 0 6px',
										fontSize: '0.95rem',
										fontWeight: 600,
										color: '#334155',
									}}
								>
									Still not working? Clear saved data and start fresh
								</p>
								<button
									type="button"
									onClick={this.resetAndStartFresh}
									style={{
										padding: '8px 20px',
										fontSize: '0.85rem',
										fontWeight: 600,
										color: '#fff',
										background: '#475569',
										border: 'none',
										borderRadius: 8,
										cursor: 'pointer',
									}}
								>
									Clear Cache &amp; Restart
								</button>
							</div>
						</div>

						{/* Step 3 */}
						<div
							style={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: 12,
								marginTop: 20,
							}}
						>
							<span
								style={{
									flexShrink: 0,
									width: 28,
									height: 28,
									borderRadius: '50%',
									background: '#e2e8f0',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: 14,
									fontWeight: 700,
									color: '#475569',
								}}
							>
								3
							</span>
							<div>
								<p
									style={{
										margin: '4px 0 6px',
										fontSize: '0.95rem',
										fontWeight: 600,
										color: '#334155',
									}}
								>
									If the problem persists, download a report and contact support
								</p>
								<p
									style={{
										fontSize: '0.8rem',
										color: '#475569',
										margin: '0 0 8px',
									}}
								>
									Please attach this file when contacting support. This report
									is sanitized and does not contain any passwords or access
									tokens.
								</p>
								<button
									type="button"
									onClick={this.generateReport}
									style={{
										padding: '8px 20px',
										fontSize: '0.85rem',
										fontWeight: 600,
										color: '#fff',
										background: '#0d9488',
										border: 'none',
										borderRadius: 8,
										cursor: 'pointer',
									}}
								>
									{this.state.reportDownloaded
										? 'Downloaded!'
										: 'Download Error Report'}
								</button>
							</div>
						</div>
					</div>

					<p style={{ fontSize: '0.8rem', color: '#475569', marginTop: 28 }}>
						Error: {this.state.error && this.state.error.toString()}
					</p>
				</div>
			</div>
		);
	}
}
